import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vehicleService, agencyService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import ImageUploader, { VehicleImageGallery } from '../components/ImageUploader';

const CarFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [agencyId, setAgencyId] = useState(null);
  const [vehicleId, setVehicleId] = useState(id || null);
  const [formData, setFormData] = useState({
    vehicle: {
      year: new Date().getFullYear(),
      make: '',
      model: '',
      trim: '',
      engine: {
        displacement: '1.6L'
      },
      transmission: 'manual',
      fuel: 'nafta',
      kilometers: 0,
      color: '',
      doors: 4
    },
    pricing: {
      currency: 'ARS',
      price: 0,
      negotiable: false
    },
    media: {
      coverUrl: '',
      images: []
    },
    description: '',
    features: ''
  });

  useEffect(() => {
    loadAgencyId();
    if (isEdit) {
      loadVehicle();
    }
  }, [id]);

  const loadAgencyId = async () => {
    try {
      if (user?.role === 'SUPER_ADMIN') {
        // For SUPER_ADMIN, we'd need to select an agency
        // For now, skip image upload for SUPER_ADMIN creating vehicles
        return;
      }
      const agency = await agencyService.getMyAgency();
      setAgencyId(agency.id);
    } catch (error) {
      console.error('Error loading agency:', error);
    }
  };

  const loadVehicle = async () => {
    try {
      const vehicle = await vehicleService.getById(id);
      
      // Parse engine - handle both old and new format
      let displacement = '1.6L';
      if (vehicle.vehicle?.engine) {
        if (vehicle.vehicle.engine.displacement) {
          displacement = vehicle.vehicle.engine.displacement;
        } else if (vehicle.vehicle.engine.displacementLiters) {
          displacement = `${vehicle.vehicle.engine.displacementLiters}L`;
        }
      }
      
      setAgencyId(vehicle.agencyId);
      setVehicleId(vehicle.id);
      setFormData({
        vehicle: {
          year: vehicle.vehicle?.year || new Date().getFullYear(),
          make: vehicle.vehicle?.make || '',
          model: vehicle.vehicle?.model || '',
          trim: vehicle.vehicle?.trim || '',
          engine: {
            displacement: displacement
          },
          transmission: vehicle.vehicle?.transmission || 'manual',
          fuel: vehicle.vehicle?.fuel || 'nafta',
          kilometers: vehicle.vehicle?.kilometers || 0,
          color: vehicle.vehicle?.color || '',
          doors: vehicle.vehicle?.doors || 4
        },
        pricing: {
          currency: vehicle.pricing?.currency || 'ARS',
          price: vehicle.pricing?.price || 0,
          negotiable: vehicle.pricing?.negotiable || false
        },
        media: {
          coverUrl: vehicle.media?.coverUrl || '',
          images: vehicle.media?.images || []
        },
        description: vehicle.description || '',
        features: (vehicle.features || []).join(', ')
      });
    } catch (error) {
      toast.error('Error al cargar vehículo');
      navigate('/app/cars');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...formData,
        features: formData.features.split(',').map(f => f.trim()).filter(Boolean)
      };

      if (isEdit) {
        await vehicleService.update(id, data);
        toast.success('Vehículo actualizado');
        navigate('/app/cars');
      } else {
        const created = await vehicleService.create(data);
        setVehicleId(created.id);
        toast.success('Vehículo creado. Ahora podés agregar imágenes');
        navigate(`/app/cars/${created.id}/edit`);
      }
    } catch (error) {
      console.error('Error:', error);
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', '));
      } else {
        toast.error('Error al guardar');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImagesChange = ({ coverUrl, images }) => {
    setFormData({
      ...formData,
      media: { coverUrl, images }
    });
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900">
          {isEdit ? 'Editar Vehículo' : 'Nuevo Vehículo'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Información del Vehículo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Marca *</label>
                <Input
                  value={formData.vehicle.make}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, make: e.target.value }
                  })}
                  placeholder="Toyota"
                  required
                  data-testid="make-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Modelo *</label>
                <Input
                  value={formData.vehicle.model}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, model: e.target.value }
                  })}
                  placeholder="Corolla"
                  required
                  data-testid="model-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Versión/Trim</label>
                <Input
                  value={formData.vehicle.trim}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, trim: e.target.value }
                  })}
                  placeholder="XEI CVT"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Año *</label>
                <Input
                  type="number"
                  value={formData.vehicle.year}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, year: parseInt(e.target.value) || 2020 }
                  })}
                  min={1950}
                  max={new Date().getFullYear() + 1}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Kilómetros *</label>
                <Input
                  type="number"
                  value={formData.vehicle.kilometers}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, kilometers: parseInt(e.target.value) || 0 }
                  })}
                  min={0}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Motor *</label>
                <Input
                  value={formData.vehicle.engine.displacement}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { 
                      ...formData.vehicle, 
                      engine: { displacement: e.target.value }
                    }
                  })}
                  placeholder="1.6L, 2.0T, 1.8L Turbo"
                  required
                  data-testid="engine-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Ej: 1.6L, 2.0T, 1.8L Turbo
                </p>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Combustible *</label>
                <Select
                  value={formData.vehicle.fuel}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, fuel: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nafta">Nafta</SelectItem>
                    <SelectItem value="diesel">Diesel</SelectItem>
                    <SelectItem value="gnc">GNC</SelectItem>
                    <SelectItem value="electrico">Eléctrico</SelectItem>
                    <SelectItem value="hibrido">Híbrido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Transmisión *</label>
                <Select
                  value={formData.vehicle.transmission}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, transmission: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="automatic">Automático</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Color</label>
                <Input
                  value={formData.vehicle.color}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, color: e.target.value }
                  })}
                  placeholder="Blanco"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Puertas</label>
                <Select
                  value={String(formData.vehicle.doors)}
                  onValueChange={(value) => setFormData({ 
                    ...formData, 
                    vehicle: { ...formData.vehicle, doors: parseInt(value) }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2 puertas</SelectItem>
                    <SelectItem value="3">3 puertas</SelectItem>
                    <SelectItem value="4">4 puertas</SelectItem>
                    <SelectItem value="5">5 puertas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Precio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Precio *</label>
                <Input
                  type="number"
                  value={formData.pricing.price}
                  onChange={(e) => setFormData({
                    ...formData,
                    pricing: { ...formData.pricing, price: parseFloat(e.target.value) || 0 }
                  })}
                  min={0}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Moneda</label>
                <Select
                  value={formData.pricing.currency}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    pricing: { ...formData.pricing, currency: value }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ARS">ARS (Pesos)</SelectItem>
                    <SelectItem value="USD">USD (Dólares)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="negotiable"
                    checked={formData.pricing.negotiable}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      pricing: { ...formData.pricing, negotiable: checked }
                    })}
                  />
                  <label htmlFor="negotiable" className="text-sm font-medium">
                    Precio negociable
                  </label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Descripción y Características</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Descripción</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe el vehículo..."
                rows={4}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Características (separadas por coma)
              </label>
              <Input
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="ABS, Aire acondicionado, Airbags, Bluetooth"
              />
            </div>
          </CardContent>
        </Card>

        {/* Image Upload Section */}
{agencyId && vehicleId && (
  <Card className="mb-6">
    <CardHeader>
      <CardTitle>Imágenes del Vehículo</CardTitle>
    </CardHeader>
    <CardContent>
      <VehicleImageGallery
        agencyId={agencyId}
        vehicleId={vehicleId}
        currentImages={formData.media.images}
        coverUrl={formData.media.coverUrl}
        onImagesChange={handleImagesChange}
        maxImages={7}
      />
    </CardContent>
  </Card>
)}

        <div className="flex gap-4">
          <Button type="submit" disabled={loading} data-testid="submit-button">
          {loading
          ? 'Guardando...'
          : isEdit
          ? 'Actualizar'
          : 'Continuar con imágenes'}          
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/cars')}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CarFormPage;